import Cookies from 'js-cookie';
import { delay } from 'msw';

import { db } from './db';

const USER_AUTH_TOKEN_COOKIE_NAME = 'mocked-bhanzu-user-auth';
const ADMIN_AUTH_TOKEN_COOKIE_NAME = 'mocked-bhanzu-admin-auth';

export const setMockUserAuthToken = (token: string) => {
  Cookies.set(USER_AUTH_TOKEN_COOKIE_NAME, token, { path: '/' });
};

export const removeAllMockAuthTokens = () => {
  Cookies.remove(USER_AUTH_TOKEN_COOKIE_NAME, { path: '/' });
  Cookies.remove(ADMIN_AUTH_TOKEN_COOKIE_NAME, { path: '/' });
};

export const encode = (obj: any) => {
  const btoa =
    typeof globalThis === 'undefined'
      ? (str: string) => Buffer.from(str, 'binary').toString('base64')
      : globalThis.btoa;
  return btoa(JSON.stringify(obj));
};

export const decode = (str: string) => {
  const atob =
    typeof globalThis === 'undefined'
      ? (str: string) => Buffer.from(str, 'base64').toString('binary')
      : globalThis.atob;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  return JSON.parse(atob(str));
};

export const hash = (str: string) => {
  let hash = 5381,
    i = str.length;

  while (i) {
    // eslint-disable-next-line unicorn/prefer-code-point
    hash = (hash * 33) ^ str.charCodeAt(--i);
  }
  return String(hash >>> 0);
};

/**
 * Stable variant id shared between the profiles mock and the
 * upcoming-session mock so sessions can be tied to the Apex Academy course.
 */
export const MOCK_APEX_VARIANT_ID = 'mock-apex-academy-variant-id';
export const MOCK_APEX_VARIANT_ID2 = 'mock-apex-academy-variant-id2';

/**
 * Batches shared between the profiles mock and the modules mock so the
 * recordings page can exercise the previous-batch segregation. The enrolled
 * course points at the current batch; older sessions carry the earlier ones.
 */
export const MOCK_CURRENT_BATCH = {
  id: 'mock-current-batch-id',
  name: 'MATH2503',
};

export const MOCK_PREVIOUS_BATCHES = [
  { id: 'mock-previous-batch-id-1', name: 'IMWdaklds' },
  { id: 'mock-previous-batch-id-2', name: 'MATH2411' },
];

export const networkDelay = () => {
  const delayTime = import.meta.env.TEST
    ? 200
    : // eslint-disable-next-line sonarjs/pseudo-random
      Math.floor(Math.random() * 700) + 300;
  return delay(delayTime);
};

export const omit = <T extends object>(obj: T, keys: string[]): T => {
  const result = {} as T;
  for (const key in obj) {
    if (!keys.includes(key)) {
      result[key] = obj[key];
    }
  }

  return result;
};

export const sanitizeUser = (user: ReturnType<typeof db.user.create>) => {
  return omit(user, ['password']);
};

export function authenticate({
  email,
  password,
}: {
  email?: string;
  password?: string;
}) {
  const user = db.user.findFirst({
    where: {
      email: {
        equals: email,
      },
    },
  });

  if (password && user?.password === hash(password)) {
    const sanitizedUser = sanitizeUser(user);
    const encodedToken = encode(sanitizedUser);
    return { user: sanitizedUser, cookie: encodedToken };
  }

  const error = new Error('Invalid username or password');
  throw error;
}

export function requireAuth(cookies: Record<string, string>) {
  try {
    const encodedToken =
      cookies[USER_AUTH_TOKEN_COOKIE_NAME] ||
      cookies[ADMIN_AUTH_TOKEN_COOKIE_NAME];
    if (!encodedToken) {
      return { error: 'Unauthorized', user: null };
    }
    const decodedToken = decode(encodedToken) as { id: string };

    const user = db.user.findFirst({
      where: {
        id: {
          equals: decodedToken.id,
        },
      },
    });

    if (!user) {
      return { error: 'Unauthorized', user: null };
    }

    return { user: sanitizeUser(user) };
  } catch {
    return { error: 'Unauthorized', user: null };
  }
}
